/**
 * ContentSlotHelper
 *
 * Design-time utilities for working with ContentSlot dictionaries on VariableOption.
 * Provides validation, content key discovery, and text resolution for builder preview.
 *
 * Runtime @variable.slotKey resolution in FunnelEngine is separate — this service
 * handles builder-side operations only.
 *
 * @module v2-variable-utils
 */

import type {
  FunnelVariable,
  VariableOption,
  ContentSlot,
} from '@segmently/schema-models/v2';
import {
  RESERVED_SLOT_KEYS,
  CONTENT_SLOT_FIELDS,
  CONTENT_SLOT_VARIABLE_TYPES,
  DEFAULT_MULTI_PICK_STRATEGY,
  type MultiPickStrategy,
  type ContentSlotField,
} from '../constants/content-slot-constants';

// ============================================================================
// VALIDATION TYPES
// ============================================================================

export interface ContentSlotValidationError {
  optionId: string;
  slotKey: string;
  field: string;
  message: string;
}

// ============================================================================
// SERVICE
// ============================================================================

export class ContentSlotHelper {
  /**
   * Check if a variable supports content slots.
   * Only enum and enum[] variables have options that can carry content.
   */
  supportsContentSlots(variable: FunnelVariable): boolean {
    return CONTENT_SLOT_VARIABLE_TYPES.includes(
      variable.type as typeof CONTENT_SLOT_VARIABLE_TYPES[number],
    );
  }

  /**
   * Check if a variable has any content slots defined on its options.
   */
  hasContentSlots(variable: FunnelVariable): boolean {
    if (!this.supportsContentSlots(variable)) return false;
    const options = (variable as { options?: VariableOption[] }).options;
    if (!options) return false;
    return options.some(
      (opt) => opt.content && Object.keys(opt.content).length > 0,
    );
  }

  /**
   * Get all unique content slot keys across all options of a variable.
   * Returns sorted array of slot key names.
   */
  getSlotKeys(variable: FunnelVariable): string[] {
    if (!this.supportsContentSlots(variable)) return [];
    const options = (variable as { options?: VariableOption[] }).options;
    if (!options) return [];

    const keys = new Set<string>();
    for (const option of options) {
      if (option.content) {
        for (const key of Object.keys(option.content)) {
          keys.add(key);
        }
      }
    }
    return Array.from(keys).sort();
  }

  /**
   * Get content slot coverage — which options have which slots.
   * Useful for builder UI to show completeness.
   *
   * @returns Map of slotKey → array of option values that have content for it
   */
  getSlotCoverage(
    variable: FunnelVariable,
  ): Record<string, string[]> {
    const coverage: Record<string, string[]> = {};
    if (!this.supportsContentSlots(variable)) return coverage;

    const options = (variable as { options?: VariableOption[] }).options;
    if (!options) return coverage;

    for (const option of options) {
      if (!option.content) continue;
      for (const slotKey of Object.keys(option.content)) {
        if (!coverage[slotKey]) coverage[slotKey] = [];
        coverage[slotKey].push(option.id);
      }
    }
    return coverage;
  }

  /**
   * Resolve text content for a single enum variable value.
   * For builder preview — shows what text would appear at runtime.
   *
   * @param variable - The enum/enum[] variable
   * @param value - The selected option value (e.g. "lose_weight")
   * @param slotKey - The content slot key (e.g. "paywall_title")
   * @param locale - The locale to resolve (e.g. "en")
   * @returns Resolved text string, or undefined if not found
   */
  resolveText(
    variable: FunnelVariable,
    value: string,
    slotKey: string,
    locale: string,
  ): string | undefined {
    if (!this.supportsContentSlots(variable)) return undefined;

    const options = (variable as { options?: VariableOption[] }).options;
    if (!options) return undefined;

    const option = options.find((o) => o.id === value);
    if (!option?.content) return undefined;

    const slot = option.content[slotKey];
    if (!slot) return undefined;

    return slot.text?.[locale] ?? slot.url;
  }

  /**
   * Resolve text content for enum[] (multi-pick) with strategy.
   *
   * @param variable - The enum[] variable
   * @param values - Array of selected option values
   * @param slotKey - The content slot key
   * @param locale - The locale to resolve
   * @param strategy - Resolution strategy: 'first', 'last', or 'all' (default)
   * @returns Resolved text string, or undefined if not found
   */
  resolveMultiText(
    variable: FunnelVariable,
    values: string[],
    slotKey: string,
    locale: string,
    strategy: MultiPickStrategy = DEFAULT_MULTI_PICK_STRATEGY,
  ): string | undefined {
    if (variable.type !== 'enum[]' || values.length === 0) return undefined;

    const options = (variable as { options?: VariableOption[] }).options;
    if (!options) return undefined;

    if (strategy === 'first') {
      return this.resolveText(variable, values[0], slotKey, locale);
    }

    if (strategy === 'last') {
      return this.resolveText(variable, values[values.length - 1], slotKey, locale);
    }

    // 'all' — comma-separated
    const resolved = values
      .map((v) => this.resolveText(variable, v, slotKey, locale))
      .filter(Boolean);

    return resolved.length > 0 ? resolved.join(', ') : undefined;
  }

  /**
   * Get the content slot for a specific option value and slot key.
   * Returns the raw ContentSlot object.
   */
  getSlot(
    variable: FunnelVariable,
    value: string,
    slotKey: string,
  ): ContentSlot | undefined {
    if (!this.supportsContentSlots(variable)) return undefined;

    const options = (variable as { options?: VariableOption[] }).options;
    if (!options) return undefined;

    const option = options.find((o) => o.id === value);
    return option?.content?.[slotKey];
  }

  /**
   * Determine the content type of a slot (which field is set).
   * Returns undefined if slot is empty or not found.
   */
  getSlotType(slot: ContentSlot): ContentSlotField | undefined {
    for (const field of CONTENT_SLOT_FIELDS) {
      if (slot[field] !== undefined && slot[field] !== null) {
        return field;
      }
    }
    return undefined;
  }

  /**
   * Validate content slots on a variable's options.
   *
   * Checks:
   * 1. Slot keys are not reserved names
   * 2. Each slot has exactly one field set
   * 3. Variable type supports content slots
   *
   * @returns Array of validation errors (empty = valid)
   */
  validate(variable: FunnelVariable): ContentSlotValidationError[] {
    const errors: ContentSlotValidationError[] = [];

    if (!this.supportsContentSlots(variable)) {
      if (this.hasAnyContent(variable)) {
        errors.push({
          optionId: '*',
          slotKey: '*',
          field: 'type',
          message: `Content slots are only supported on enum and enum[] variables, not "${variable.type}"`,
        });
      }
      return errors;
    }

    const options = (variable as { options?: VariableOption[] }).options;
    if (!options) return errors;

    for (const option of options) {
      if (!option.content) continue;

      for (const [slotKey, slot] of Object.entries(option.content)) {
        // Check reserved keys
        if (RESERVED_SLOT_KEYS.includes(slotKey as typeof RESERVED_SLOT_KEYS[number])) {
          errors.push({
            optionId: option.id,
            slotKey,
            field: 'slotKey',
            message: `Slot key "${slotKey}" is reserved (reserved keys: ${RESERVED_SLOT_KEYS.join(', ')})`,
          });
        }

        // Check exactly one field is set
        const setFields = CONTENT_SLOT_FIELDS.filter(
          (f) => slot[f] !== undefined && slot[f] !== null,
        );
        if (setFields.length === 0) {
          errors.push({
            optionId: option.id,
            slotKey,
            field: 'content',
            message: 'Content slot is empty — exactly one field (text, image, clip, url) must be set',
          });
        } else if (setFields.length > 1) {
          errors.push({
            optionId: option.id,
            slotKey,
            field: 'content',
            message: `Content slot has ${setFields.length} fields set (${setFields.join(', ')}) — exactly one is allowed`,
          });
        }
      }
    }

    return errors;
  }

  /**
   * Check if a slot key is reserved.
   */
  isReservedKey(key: string): boolean {
    return RESERVED_SLOT_KEYS.includes(key as typeof RESERVED_SLOT_KEYS[number]);
  }

  // ─── Private ──────────────────────────────────────────────────────

  private hasAnyContent(variable: FunnelVariable): boolean {
    const options = (variable as { options?: VariableOption[] }).options;
    if (!options) return false;
    return options.some(
      (opt) => opt.content && Object.keys(opt.content).length > 0,
    );
  }
}

/** Singleton instance */
export const contentSlotHelper = new ContentSlotHelper();
